/**
 * @file Composable for computing collapse-around-touch-point calculations during drag and drop.
 *
 * This composable provides the logic to calculate padding and scroll positions that
 * position the collapsed placeholder of a dragged post right under the pointer,
 * with appropriate spacing above and below the scroll area.
 */

import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { storeToRefs } from 'pinia'
import { nextTick, watch, type Ref } from 'vue'

interface UseCollapseAroundTouchPointOnDragStartOptions {
  scrollContainerElementRef: Ref<HTMLElement | null | undefined>
  postContainerDragPaddingRef: Ref<{ paddingTop?: string; paddingBottom?: string }>
  triggerRef: Ref<boolean> | Ref<boolean>
}

export function useCollapseAroundTouchPointOnDragStart({
  scrollContainerElementRef,
  postContainerDragPaddingRef,
  triggerRef,
}: UseCollapseAroundTouchPointOnDragStartOptions) {
  const surfacePostsDragAndDropStore = useSurfacePostsDragAndDropStore()
  const { dragStartEventPayload } = storeToRefs(surfacePostsDragAndDropStore)

  const collapseAroundTouchPointArgs = (scrollableContainerEl: HTMLElement) => {
    const clientY = dragStartEventPayload.value?.location.current.input?.clientY
    const compressedPostEl = (dragStartEventPayload.value?.source.data.data as { compressedPostEl: HTMLElement })
      ?.compressedPostEl

    if (clientY === undefined || !compressedPostEl) {
      return { spaceToAddAbove: undefined, spaceToAddBelow: undefined, scrollTop: undefined }
    }

    const container = scrollableContainerEl.getBoundingClientRect()
    const { scrollHeight, scrollTop } = scrollableContainerEl

    const post = compressedPostEl.getBoundingClientRect()
    const postHeight = post.height
    const postFinalTop = clientY - postHeight / 2
    const postFinalBottom = clientY + postHeight / 2

    const spaceProvidedAbove = post.top - (container.top - scrollTop)
    const spaceProvidedBelow = scrollHeight - spaceProvidedAbove - postHeight
    const spaceNeededAbove = postFinalTop - container.top
    const spaceNeededBelow = container.bottom - postFinalBottom

    return {
      spaceToAddAbove: Math.max(0, spaceNeededAbove - spaceProvidedAbove),
      spaceToAddBelow: Math.max(0, spaceNeededBelow - spaceProvidedBelow),
      scrollTop: spaceProvidedAbove - (postFinalTop - container.top),
    }
  }

  const setDragPaddingAndScroll = async () => {
    const postsContainer = scrollContainerElementRef.value
    if (!postsContainer) return

    // Compute padding and scrollTop only after posts have collapsed
    let scrollTopAfterCompression
    await nextTick(() => {
      const { scrollTop, spaceToAddAbove, spaceToAddBelow } = collapseAroundTouchPointArgs(postsContainer)
      if (spaceToAddAbove) postContainerDragPaddingRef.value.paddingTop = `${spaceToAddAbove}px`
      if (spaceToAddBelow) postContainerDragPaddingRef.value.paddingBottom = `${spaceToAddBelow}px`
      scrollTopAfterCompression = scrollTop
    })

    // Scroll only after padding has been added
    await nextTick(() => {
      if (scrollTopAfterCompression !== undefined) {
        postsContainer.scrollTop = scrollTopAfterCompression
      }
    })
  }

  const clearDragPadding = () => {
    // Be careful not to zero out any preexisting padding
    postContainerDragPaddingRef.value = {}
  }

  // Watch for drag state changes and apply/clear padding accordingly
  watch(triggerRef, async (isDragging) => {
    if (isDragging) {
      await setDragPaddingAndScroll()
    } else {
      clearDragPadding()
    }
  })
}
